import React, { useState, useEffect } from 'react';
import { Country, State, City } from 'country-state-city';

const LocationSelector = ({ 
    country, 
    state, 
    localGovernment, 
    onChange, 
    required = false,
    className = ""
}) => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    const [selectedCountryCode, setSelectedCountryCode] = useState('');
    const [selectedStateCode, setSelectedStateCode] = useState('');

    // Load countries on mount
    useEffect(() => {
        const allCountries = Country.getAllCountries();
        setCountries(allCountries);
        
        // If an initial country name is provided, find its code to load states
        if (country) {
            const matchedCountry = allCountries.find(c => c.name === country);
            if (matchedCountry) {
                setSelectedCountryCode(matchedCountry.isoCode);
            }
        }
    }, []);

    // Load states when country changes
    useEffect(() => {
        if (selectedCountryCode) {
            const countryStates = State.getStatesOfCountry(selectedCountryCode);
            setStates(countryStates);
            
            // If an initial state is provided, find its code to load cities
            if (state) {
                const matchedState = countryStates.find(s => s.name === state);
                if (matchedState) {
                    setSelectedStateCode(matchedState.isoCode);
                }
            } else {
                setCities([]);
                setSelectedStateCode('');
            }
        } else {
            setStates([]);
            setCities([]);
        }
    }, [selectedCountryCode, state]);

    // Load cities when state changes
    useEffect(() => {
        if (selectedCountryCode && selectedStateCode) {
            const stateCities = City.getCitiesOfState(selectedCountryCode, selectedStateCode);
            setCities(stateCities);
        } else {
            setCities([]);
        }
    }, [selectedCountryCode, selectedStateCode]);

    const handleCountryChange = (e) => {
        const selectedCode = e.target.value;
        setSelectedCountryCode(selectedCode);
        setSelectedStateCode('');
        
        const selectedCountry = countries.find(c => c.isoCode === selectedCode);
        const countryName = selectedCountry ? selectedCountry.name : '';
        
        onChange('country', countryName);
        onChange('state', '');
        onChange('localGovernment', '');
    };

    const handleStateChange = (e) => {
        const selectedCode = e.target.value;
        setSelectedStateCode(selectedCode);
        
        const selectedStateObj = states.find(s => s.isoCode === selectedCode);
        const stateName = selectedStateObj ? selectedStateObj.name : '';
        
        onChange('state', stateName);
        onChange('localGovernment', '');
    };

    const handleCityChange = (e) => {
        const cityName = e.target.value;
        onChange('localGovernment', cityName);
    };

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Country {required && '*'}
                </label>
                <select 
                    className="form-input bg-white" 
                    value={selectedCountryCode || ''} 
                    onChange={handleCountryChange}
                    required={required}
                >
                    <option value="">Select Country</option>
                    {countries.map(c => (
                        <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    State / Province {required && '*'}
                </label>
                <select 
                    className="form-input bg-white disabled:bg-gray-100" 
                    value={selectedStateCode || ''} 
                    onChange={handleStateChange}
                    disabled={!selectedCountryCode || states.length === 0}
                    required={required && states.length > 0}
                >
                    <option value="">Select State</option>
                    {states.map(s => (
                        <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Local Government / City {required && '*'}
                </label>
                <select 
                    className="form-input bg-white disabled:bg-gray-100" 
                    value={localGovernment || ''} 
                    onChange={handleCityChange}
                    disabled={!selectedStateCode || cities.length === 0}
                    required={required && cities.length > 0}
                >
                    <option value="">Select Local Government / City</option>
                    {cities.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default LocationSelector;
